import { Briefcase, Calendar, GraduationCap, Languages, MapPin, Star } from "lucide-react";
import { Link } from "react-router-dom";
import type { Candidate } from "../data/mock-candidates";

type CandidateCardProps = {
  candidate: Candidate;
};

function MemberBadge({ type }: { type: "HIGHCLASS" | "TALENT" }) {
  if (type === "HIGHCLASS") {
    return (
      <span className="rounded-sm bg-amber-900 px-1.5 py-0.5 font-bold text-[11px] text-amber-100 tracking-wider">
        HIGHCLASS
      </span>
    );
  }
  return (
    <span className="rounded-sm bg-teal-600 px-1.5 py-0.5 font-bold text-[11px] text-white tracking-wider">
      TALENT
    </span>
  );
}

function HotBadge() {
  return (
    <span className="rounded-sm bg-red-500 px-1.5 py-0.5 font-bold text-[11px] text-white tracking-wider">
      HOT
    </span>
  );
}

function ResponseIndicator() {
  const level = Math.floor(Math.random() * 4) + 1;
  return (
    <div className="flex gap-0.5" title={`返信率: レベル${level}`}>
      {Array.from({ length: 4 }, (_, i) => (
        <div
          className={`h-2.5 w-1 rounded-sm ${i < level ? "bg-purple-500" : "bg-gray-200"}`}
          key={`bar-${i.toString()}`}
        />
      ))}
    </div>
  );
}

export function CandidateCard({ candidate }: CandidateCardProps) {
  const isNew = candidate.lastLoginDate >= "2026-03-16";

  return (
    <div className="flex items-start border-gray-200 border-b bg-white px-4 py-3 last:border-b-0 hover:bg-gray-50">
      {/* チェックボックス */}
      <div className="mr-3 pt-1">
        <input
          className="h-4 w-4 accent-purple-600"
          onClick={(e) => e.stopPropagation()}
          type="checkbox"
        />
      </div>

      {/* アバター */}
      <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-400">
        <span className="text-xs">{candidate.gender === "男性" ? "M" : "F"}</span>
      </div>

      {/* メイン情報 */}
      <Link className="flex-1" to={`/direct-recruiting-sample/candidates/${candidate.id}`}>
        <div className="flex items-center gap-1.5">
          <MemberBadge type={candidate.memberType} />
          {candidate.isHot ? <HotBadge /> : null}
          {isNew ? (
            <span className="rounded-sm bg-blue-500 px-1.5 py-0.5 font-bold text-[11px] text-white">
              新着
            </span>
          ) : null}
          <ResponseIndicator />
          <span className="ml-1 text-gray-400 text-xs">{candidate.memberId}</span>
        </div>

        <div className="mt-1 flex items-center gap-2 text-sm text-gray-800">
          {candidate.age ? (
            <span className="font-medium">{candidate.age}歳</span>
          ) : candidate.estimatedAge ? (
            <span
              className="font-medium text-green-600"
              title="スカウトメールAIによる分析の推定年齢です"
            >
              推定{candidate.estimatedAge}歳
            </span>
          ) : null}
          <span className="text-gray-300">|</span>
          <span>{candidate.gender}</span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-0.5">
            <MapPin className="h-3 w-3 text-gray-400" />
            {candidate.prefecture}
          </span>
          <span className="text-gray-300">|</span>
          <span className="font-medium">年収 {candidate.currentSalary}万円</span>
        </div>

        <div className="mt-1 flex items-center gap-1.5 text-[13px] text-gray-600">
          <Briefcase className="h-3 w-3 text-gray-400" />
          <span>
            {candidate.currentCompany || "（離職中）"}
            {candidate.currentDepartment
              ? ` / ${candidate.currentDepartment} / ${candidate.currentPosition}`
              : ""}
          </span>
        </div>

        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="text-gray-500">{candidate.jobCategory}</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">{candidate.industry}</span>
        </div>

        <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-0.5">
            <GraduationCap className="h-3 w-3" />
            {candidate.education.school}（{candidate.education.degree}）
          </span>
          <span className="flex items-center gap-0.5">
            <Languages className="h-3 w-3" />
            {candidate.languages.map((l) => l.language).join("・")}
          </span>
          <span className="flex items-center gap-0.5">
            <Calendar className="h-3 w-3" />
            {candidate.lastLoginDate}
          </span>
        </div>
      </Link>

      {/* 右アクション */}
      <div className="ml-2 flex shrink-0 flex-col items-center gap-1 pt-1">
        <button
          className="rounded p-1.5 text-gray-300 hover:bg-purple-50 hover:text-purple-500"
          onClick={(e) => e.stopPropagation()}
          title="気になる"
          type="button"
        >
          <Star className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
